import { createElement, type ComponentType } from "react";
import {
  AirVent,
  Album,
  Ambulance,
  Apple,
  Archive,
  Armchair,
  ArrowLeftRight,
  Backpack,
  BadgeCheck,
  BadgeCent,
  BadgeDollarSign,
  BadgeEuro,
  BadgePoundSterling,
  BadgeRussianRuble,
  BadgeSwissFranc,
  Baby,
  BaggageClaim,
  Banknote,
  BanknoteArrowDown,
  BanknoteArrowUp,
  Barcode,
  Bath,
  Beef,
  Beer,
  Bike,
  Bitcoin,
  Blinds,
  BookOpen,
  BookOpenText,
  BottleWine,
  Box,
  Boxes,
  BriefcaseMedical,
  Bot,
  Brain,
  BriefcaseBusiness,
  BrickWall,
  Building,
  Building2,
  Bus,
  BusFront,
  Cake,
  CakeSlice,
  Calculator,
  Camera,
  CalendarDays,
  Car,
  CarFront,
  CarTaxiFront,
  Caravan,
  Carrot,
  ChartNoAxesCombined,
  ChartPie,
  ChefHat,
  Cherry,
  CircleQuestionMark,
  CircleDollarSign,
  Clipboard,
  ClipboardList,
  Clock3,
  Cloud,
  Clapperboard,
  Coffee,
  Cog,
  Coins,
  Computer,
  Construction,
  Cookie,
  CookingPot,
  CreditCard,
  Cross,
  Crown,
  CupSoda,
  Currency,
  Dice5,
  DollarSign,
  Download,
  Drama,
  Drill,
  Dumbbell,
  EggFried,
  Euro,
  Fan,
  FerrisWheel,
  FileSpreadsheet,
  Fish,
  Flower,
  Folder,
  Footprints,
  ForkKnife,
  Fuel,
  Gamepad2,
  Glasses,
  Gem,
  Gift,
  GraduationCap,
  Grape,
  Grid3X3,
  Guitar,
  Hammer,
  HandCoins,
  HandHeart,
  Hamburger,
  Headphones,
  Heart,
  HeartPulse,
  Heater,
  HelpingHand,
  Hospital,
  Hotel,
  House,
  HousePlug,
  HouseWifi,
  IceCream,
  IdCard,
  Key,
  KeyRound,
  Lamp,
  LampDesk,
  Landmark,
  Laptop,
  Layers,
  Leaf,
  Lightbulb,
  LogOut,
  Luggage,
  Mail,
  MapPin,
  Medal,
  Menu,
  Mic,
  Microwave,
  Milk,
  Monitor,
  Motorbike,
  MousePointerClick,
  Music,
  Network,
  Newspaper,
  Notebook,
  Package,
  PackageCheck,
  PackageOpen,
  PaintBucket,
  Paintbrush,
  Palette,
  Palmtree,
  PartyPopper,
  PawPrint,
  Percent,
  Phone,
  PiggyBank,
  Pill,
  PillBottle,
  Plane,
  PlaneLanding,
  PlaneTakeoff,
  PlugZap,
  Popcorn,
  PoundSterling,
  Presentation,
  QrCode,
  Radio,
  ReceiptText,
  Receipt,
  Recycle,
  Refrigerator,
  Repeat,
  Road,
  Router,
  Route,
  Ruler,
  RussianRuble,
  Sailboat,
  Salad,
  Sandwich,
  Scale,
  ScanBarcode,
  School,
  Scooter,
  Scissors,
  Settings,
  ShieldCheck,
  Ship,
  Shirt,
  ShoppingBag,
  ShoppingBasket,
  ShoppingCart,
  Smile,
  Smartphone,
  Soup,
  Sofa,
  SquareParking,
  Store,
  Stethoscope,
  Syringe,
  Tablet,
  Tag,
  Tags,
  TentTree,
  Theater,
  Ticket,
  TicketPercent,
  TicketsPlane,
  Train,
  TrainFront,
  TrainTrack,
  TramFront,
  TrendingDown,
  TrendingUp,
  Trophy,
  Truck,
  Tv,
  Umbrella,
  University,
  Undo2,
  User,
  UserRound,
  Users,
  UsersRound,
  Utensils,
  UtensilsCrossed,
  Vault,
  Wallet,
  WalletCards,
  WalletMinimal,
  WashingMachine,
  Wheat,
  Wifi,
  Wine,
  Wrench,
  Zap,
} from "lucide-react";

type CategoryIconComponent = ComponentType<{ "aria-hidden"?: boolean | "true" | "false"; className?: string }>;

export type CategoryIconOption = {
  id: string;
  label: string;
  Icon: CategoryIconComponent;
};

export type CategoryIconGroup = {
  title: string;
  icons: CategoryIconOption[];
};

function bankImageIcon(src: string, label: string): CategoryIconComponent {
  return function BankImageIcon(props) {
    return createElement("img", { ...props, src, alt: "", title: label });
  };
}

const BocIcon = bankImageIcon("/static/bank-icons/favicon_boc.ico", "Bank of Cyprus");
const RevolutIcon = bankImageIcon("/static/bank-icons/favicon_revolut.svg", "Revolut");
const RaiffeisenIcon = bankImageIcon("/static/bank-icons/favicon_raiffeisen.png", "Raiffeisen Russia");

export const categoryIconOptions: CategoryIconOption[] = [
  { id: "tag", label: "Tag", Icon: Tag },
  { id: "key-list", label: "Key list", Icon: KeyRound },
  { id: "airplane", label: "Airplane", Icon: Plane },
  { id: "buildings", label: "Buildings", Icon: Building2 },
  { id: "hands", label: "Hands", Icon: HandHeart },
  { id: "verified", label: "Verified", Icon: BadgeCheck },
  { id: "logout", label: "Exit", Icon: LogOut },
  { id: "arrows-left-right", label: "Transfer", Icon: ArrowLeftRight },
  { id: "bank", label: "Bank", Icon: Landmark },
  { id: "boc", label: "Bank of Cyprus", Icon: BocIcon },
  { id: "revolut", label: "Revolut", Icon: RevolutIcon },
  { id: "raiffeisen", label: "Raiffeisen Russia", Icon: RaiffeisenIcon },
  { id: "cash-down", label: "Cash down", Icon: BadgeDollarSign },
  { id: "cash-stack", label: "Cash stack", Icon: Banknote },
  { id: "cash-time", label: "Cash time", Icon: Clock3 },
  { id: "cash-scissors", label: "Cash cut", Icon: Scissors },
  { id: "cash-up", label: "Cash up", Icon: BadgeDollarSign },
  { id: "wine", label: "Wine", Icon: Wine },
  { id: "dumbbell", label: "Fitness", Icon: Dumbbell },
  { id: "island", label: "Vacation", Icon: Umbrella },
  { id: "five-coins", label: "Coins", Icon: Coins },
  { id: "cake", label: "Cake", Icon: Cake },
  { id: "invoice", label: "Invoice", Icon: ReceiptText },
  { id: "book", label: "Book", Icon: BookOpen },
  { id: "book-download", label: "Download book", Icon: BookOpen },
  { id: "archive", label: "Archive", Icon: Archive },
  { id: "lightning", label: "Electricity", Icon: Zap },
  { id: "chart-up", label: "Growth", Icon: ChartNoAxesCombined },
  { id: "storefront", label: "Store", Icon: Store },
  { id: "bus", label: "Bus", Icon: Bus },
  { id: "briefcase", label: "Briefcase", Icon: BriefcaseBusiness },
  { id: "car", label: "Car", Icon: Car },
  { id: "paw", label: "Pets", Icon: PawPrint },
  { id: "signal", label: "Signal", Icon: Wifi },
  { id: "card-pen", label: "Card note", Icon: CreditCard },
  { id: "dots", label: "Dots", Icon: Grid3X3 },
  { id: "cog", label: "Settings", Icon: Settings },
  { id: "cogs", label: "Gears", Icon: Cog },
  { id: "coins", label: "Coins stack", Icon: Coins },
  { id: "sofa", label: "Furniture", Icon: Sofa },
  { id: "coffee", label: "Coffee", Icon: Coffee },
  { id: "network-money", label: "Money network", Icon: Network },
  { id: "ruler-pencil", label: "Tools", Icon: Ruler },
  { id: "diamond", label: "Diamond", Icon: Gem },
  { id: "dice", label: "Games", Icon: Dice5 },
  { id: "monitor", label: "Monitor", Icon: Monitor },
  { id: "doctor", label: "Doctor", Icon: Stethoscope },
  { id: "id-card", label: "ID card", Icon: IdCard },
  { id: "robot-arm", label: "Robot arm", Icon: Bot },
  { id: "menu", label: "Menu", Icon: Menu },
  { id: "envelope", label: "Envelope", Icon: Mail },
  { id: "money-bag", label: "Money bag", Icon: HandCoins },
  { id: "piggy-bank", label: "Piggy bank", Icon: PiggyBank },
  { id: "wallet", label: "Wallet", Icon: Wallet },
  { id: "hand-tap", label: "Tap", Icon: MousePointerClick },
  { id: "newspaper", label: "Newspaper", Icon: Newspaper },
  { id: "building", label: "Building", Icon: Building },
  { id: "parking", label: "Parking", Icon: SquareParking },
  { id: "cash", label: "Cash", Icon: Banknote },
  { id: "people", label: "People", Icon: Users },
  { id: "percent", label: "Percent", Icon: Percent },
  { id: "person-refresh", label: "Person refresh", Icon: Repeat },
  { id: "phone", label: "Phone", Icon: Phone },
  { id: "layers", label: "Layers", Icon: Layers },
  { id: "question", label: "Question", Icon: CircleQuestionMark },
  { id: "hand-coins", label: "Hand coins", Icon: HandCoins },
  { id: "food", label: "Food", Icon: Utensils },
  { id: "tv", label: "TV", Icon: Tv },
  { id: "undo", label: "Undo", Icon: Undo2 },
  { id: "shirt", label: "Clothes", Icon: Shirt },
  { id: "safe", label: "Safe", Icon: ShieldCheck },
  { id: "person", label: "Person", Icon: User },
  { id: "scales", label: "Scales", Icon: Scale },
  { id: "bank-home", label: "Mortgage", Icon: House },
  { id: "cloche", label: "Restaurant", Icon: Utensils },
  { id: "shopping-bag", label: "Shopping bag", Icon: ShoppingBag },
  { id: "basket", label: "Basket", Icon: ShoppingBasket },
  { id: "cart-in", label: "Cart in", Icon: ShoppingCart },
  { id: "cart-out", label: "Cart out", Icon: ShoppingCart },
  { id: "download-tray", label: "Download", Icon: Download },
  { id: "flower", label: "Flower", Icon: Flower },
  { id: "stroller", label: "Stroller", Icon: Baby },
  { id: "graduation", label: "Education", Icon: GraduationCap },
  { id: "wrench", label: "Repair", Icon: Wrench },
  { id: "gift", label: "Gift", Icon: Gift },
  { id: "tools", label: "Tools crossed", Icon: Hammer },
  { id: "home", label: "Home", Icon: House },
  { id: "train", label: "Train", Icon: Train },
  { id: "taxi", label: "Taxi", Icon: Car },
  { id: "walking", label: "Walking", Icon: User },
  { id: "grocery", label: "Groceries", Icon: ShoppingCart },
  { id: "wifi", label: "Wi-Fi", Icon: Wifi },
  { id: "bottle", label: "Bottle", Icon: Wine },
  { id: "user-tie", label: "Professional", Icon: BriefcaseBusiness },
  { id: "fuel", label: "Fuel", Icon: Fuel },
  { id: "smile", label: "Smile", Icon: Smile },
  { id: "recycle", label: "Recycle", Icon: Recycle },
  { id: "head", label: "Mind", Icon: Brain },
  { id: "key", label: "Key", Icon: Key },
  { id: "clipboard", label: "Clipboard", Icon: Clipboard },
  { id: "calendar-star", label: "Calendar", Icon: CalendarDays },
  { id: "savings", label: "Savings", Icon: Landmark },
  { id: "health", label: "Health", Icon: HeartPulse },
  { id: "salary", label: "Salary", Icon: Banknote },
  { id: "transport", label: "Transport", Icon: Car },
  { id: "badge-cent", label: "Cents", Icon: BadgeCent },
  { id: "badge-dollar", label: "Dollar", Icon: BadgeDollarSign },
  { id: "badge-euro", label: "Euro", Icon: BadgeEuro },
  { id: "badge-pound", label: "Pound", Icon: BadgePoundSterling },
  { id: "badge-ruble", label: "Ruble", Icon: BadgeRussianRuble },
  { id: "badge-franc", label: "Swiss franc", Icon: BadgeSwissFranc },
  { id: "banknote-in", label: "Cash income", Icon: BanknoteArrowDown },
  { id: "banknote-out", label: "Cash expense", Icon: BanknoteArrowUp },
  { id: "bitcoin", label: "Bitcoin", Icon: Bitcoin },
  { id: "circle-dollar", label: "Dollar circle", Icon: CircleDollarSign },
  { id: "currency", label: "Currency", Icon: Currency },
  { id: "dollar", label: "Dollar sign", Icon: DollarSign },
  { id: "euro", label: "Euro sign", Icon: Euro },
  { id: "pound", label: "Pound sign", Icon: PoundSterling },
  { id: "ruble", label: "Ruble sign", Icon: RussianRuble },
  { id: "receipt", label: "Receipt", Icon: Receipt },
  { id: "calculator", label: "Calculator", Icon: Calculator },
  { id: "chart-pie", label: "Pie chart", Icon: ChartPie },
  { id: "trending-up", label: "Trending up", Icon: TrendingUp },
  { id: "trending-down", label: "Trending down", Icon: TrendingDown },
  { id: "wallet-cards", label: "Wallet cards", Icon: WalletCards },
  { id: "wallet-minimal", label: "Wallet minimal", Icon: WalletMinimal },
  { id: "vault", label: "Vault", Icon: Vault },
  { id: "university", label: "University", Icon: University },
  { id: "apple", label: "Apple", Icon: Apple },
  { id: "beef", label: "Meat", Icon: Beef },
  { id: "beer", label: "Beer", Icon: Beer },
  { id: "bottle-wine", label: "Wine bottle", Icon: BottleWine },
  { id: "cake-slice", label: "Cake slice", Icon: CakeSlice },
  { id: "carrot", label: "Vegetables", Icon: Carrot },
  { id: "chef-hat", label: "Chef", Icon: ChefHat },
  { id: "cherry", label: "Fruit", Icon: Cherry },
  { id: "cookie", label: "Cookie", Icon: Cookie },
  { id: "cooking-pot", label: "Cooking", Icon: CookingPot },
  { id: "cup-soda", label: "Drinks", Icon: CupSoda },
  { id: "egg-fried", label: "Breakfast", Icon: EggFried },
  { id: "fish", label: "Fish", Icon: Fish },
  { id: "fork-knife", label: "Dining", Icon: ForkKnife },
  { id: "grape", label: "Grapes", Icon: Grape },
  { id: "hamburger", label: "Fast food", Icon: Hamburger },
  { id: "ice-cream", label: "Ice cream", Icon: IceCream },
  { id: "milk", label: "Milk", Icon: Milk },
  { id: "salad", label: "Salad", Icon: Salad },
  { id: "sandwich", label: "Sandwich", Icon: Sandwich },
  { id: "soup", label: "Soup", Icon: Soup },
  { id: "utensils-crossed", label: "Restaurant", Icon: UtensilsCrossed },
  { id: "wheat", label: "Bakery", Icon: Wheat },
  { id: "baggage-claim", label: "Baggage", Icon: BaggageClaim },
  { id: "bike", label: "Bike", Icon: Bike },
  { id: "bus-front", label: "Bus front", Icon: BusFront },
  { id: "car-front", label: "Car front", Icon: CarFront },
  { id: "car-taxi", label: "Taxi", Icon: CarTaxiFront },
  { id: "caravan", label: "Caravan", Icon: Caravan },
  { id: "luggage", label: "Luggage", Icon: Luggage },
  { id: "map-pin", label: "Location", Icon: MapPin },
  { id: "motorbike", label: "Motorbike", Icon: Motorbike },
  { id: "plane-landing", label: "Flight landing", Icon: PlaneLanding },
  { id: "plane-takeoff", label: "Flight takeoff", Icon: PlaneTakeoff },
  { id: "road", label: "Road", Icon: Road },
  { id: "route", label: "Route", Icon: Route },
  { id: "sailboat", label: "Sailboat", Icon: Sailboat },
  { id: "scooter", label: "Scooter", Icon: Scooter },
  { id: "ship", label: "Ship", Icon: Ship },
  { id: "tickets-plane", label: "Flight tickets", Icon: TicketsPlane },
  { id: "train-front", label: "Train front", Icon: TrainFront },
  { id: "train-track", label: "Train track", Icon: TrainTrack },
  { id: "tram", label: "Tram", Icon: TramFront },
  { id: "truck", label: "Truck", Icon: Truck },
  { id: "hotel", label: "Hotel", Icon: Hotel },
  { id: "palmtree", label: "Palm tree", Icon: Palmtree },
  { id: "tent-tree", label: "Camping", Icon: TentTree },
  { id: "air-vent", label: "Air conditioning", Icon: AirVent },
  { id: "armchair", label: "Armchair", Icon: Armchair },
  { id: "bath", label: "Bathroom", Icon: Bath },
  { id: "blinds", label: "Blinds", Icon: Blinds },
  { id: "brick-wall", label: "Renovation", Icon: BrickWall },
  { id: "construction", label: "Construction", Icon: Construction },
  { id: "drill", label: "Drill", Icon: Drill },
  { id: "fan", label: "Fan", Icon: Fan },
  { id: "heater", label: "Heating", Icon: Heater },
  { id: "house-plug", label: "Home utilities", Icon: HousePlug },
  { id: "house-wifi", label: "Home internet", Icon: HouseWifi },
  { id: "lamp", label: "Lamp", Icon: Lamp },
  { id: "lamp-desk", label: "Desk lamp", Icon: LampDesk },
  { id: "lightbulb", label: "Light", Icon: Lightbulb },
  { id: "microwave", label: "Microwave", Icon: Microwave },
  { id: "paint-bucket", label: "Paint", Icon: PaintBucket },
  { id: "paintbrush", label: "Paintbrush", Icon: Paintbrush },
  { id: "plug-zap", label: "Electricity", Icon: PlugZap },
  { id: "refrigerator", label: "Refrigerator", Icon: Refrigerator },
  { id: "washing-machine", label: "Laundry", Icon: WashingMachine },
  { id: "ambulance", label: "Ambulance", Icon: Ambulance },
  { id: "briefcase-medical", label: "Medical kit", Icon: BriefcaseMedical },
  { id: "cross", label: "Medical", Icon: Cross },
  { id: "heart", label: "Heart", Icon: Heart },
  { id: "hospital", label: "Hospital", Icon: Hospital },
  { id: "pill", label: "Pill", Icon: Pill },
  { id: "pill-bottle", label: "Medicine", Icon: PillBottle },
  { id: "syringe", label: "Syringe", Icon: Syringe },
  { id: "baby", label: "Baby", Icon: Baby },
  { id: "backpack", label: "Backpack", Icon: Backpack },
  { id: "footprints", label: "Walking", Icon: Footprints },
  { id: "glasses", label: "Glasses", Icon: Glasses },
  { id: "helping-hand", label: "Help", Icon: HelpingHand },
  { id: "medal", label: "Medal", Icon: Medal },
  { id: "school", label: "School", Icon: School },
  { id: "trophy", label: "Trophy", Icon: Trophy },
  { id: "user-round", label: "User", Icon: UserRound },
  { id: "users-round", label: "Family", Icon: UsersRound },
  { id: "barcode", label: "Barcode", Icon: Barcode },
  { id: "box", label: "Box", Icon: Box },
  { id: "boxes", label: "Boxes", Icon: Boxes },
  { id: "package", label: "Package", Icon: Package },
  { id: "package-check", label: "Delivered", Icon: PackageCheck },
  { id: "package-open", label: "Open package", Icon: PackageOpen },
  { id: "qr-code", label: "QR code", Icon: QrCode },
  { id: "scan-barcode", label: "Scan barcode", Icon: ScanBarcode },
  { id: "tags", label: "Tags", Icon: Tags },
  { id: "ticket", label: "Ticket", Icon: Ticket },
  { id: "ticket-percent", label: "Discount ticket", Icon: TicketPercent },
  { id: "album", label: "Album", Icon: Album },
  { id: "book-open-text", label: "Reading", Icon: BookOpenText },
  { id: "camera", label: "Camera", Icon: Camera },
  { id: "clapperboard", label: "Movies", Icon: Clapperboard },
  { id: "drama", label: "Theater", Icon: Drama },
  { id: "ferris-wheel", label: "Amusement", Icon: FerrisWheel },
  { id: "gamepad", label: "Games", Icon: Gamepad2 },
  { id: "guitar", label: "Music", Icon: Guitar },
  { id: "headphones", label: "Headphones", Icon: Headphones },
  { id: "mic", label: "Microphone", Icon: Mic },
  { id: "music", label: "Music note", Icon: Music },
  { id: "party-popper", label: "Party", Icon: PartyPopper },
  { id: "popcorn", label: "Cinema", Icon: Popcorn },
  { id: "theater", label: "Theater", Icon: Theater },
  { id: "cloud", label: "Cloud", Icon: Cloud },
  { id: "computer", label: "Computer", Icon: Computer },
  { id: "laptop", label: "Laptop", Icon: Laptop },
  { id: "radio", label: "Radio", Icon: Radio },
  { id: "router", label: "Router", Icon: Router },
  { id: "smartphone", label: "Smartphone", Icon: Smartphone },
  { id: "tablet", label: "Tablet", Icon: Tablet },
  { id: "bot", label: "Automation", Icon: Bot },
  { id: "building-2", label: "Office", Icon: Building2 },
  { id: "clipboard-list", label: "Checklist", Icon: ClipboardList },
  { id: "file-spreadsheet", label: "Spreadsheet", Icon: FileSpreadsheet },
  { id: "folder", label: "Folder", Icon: Folder },
  { id: "notebook", label: "Notebook", Icon: Notebook },
  { id: "presentation", label: "Presentation", Icon: Presentation },
  { id: "crown", label: "Premium", Icon: Crown },
  { id: "leaf", label: "Eco", Icon: Leaf },
  { id: "palette", label: "Art", Icon: Palette },
];

const categoryIconGroupDefinitions = [
  {
    title: "Banks",
    ids: ["boc", "revolut", "raiffeisen"],
  },
  {
    title: "Food",
    ids: [
      "food",
      "cloche",
      "grocery",
      "coffee",
      "cake",
      "wine",
      "bottle",
      "apple",
      "beef",
      "beer",
      "bottle-wine",
      "cake-slice",
      "carrot",
      "chef-hat",
      "cherry",
      "cookie",
      "cooking-pot",
      "cup-soda",
      "egg-fried",
      "fish",
      "fork-knife",
      "grape",
      "hamburger",
      "ice-cream",
      "milk",
      "salad",
      "sandwich",
      "soup",
      "utensils-crossed",
      "wheat",
    ],
  },
  {
    title: "Transport",
    ids: [
      "airplane",
      "bus",
      "car",
      "parking",
      "train",
      "taxi",
      "walking",
      "fuel",
      "transport",
      "baggage-claim",
      "bike",
      "bus-front",
      "car-front",
      "car-taxi",
      "caravan",
      "luggage",
      "map-pin",
      "motorbike",
      "plane-landing",
      "plane-takeoff",
      "road",
      "route",
      "sailboat",
      "scooter",
      "ship",
      "tickets-plane",
      "train-front",
      "train-track",
      "tram",
      "truck",
    ],
  },
  {
    title: "Finance",
    ids: [
      "arrows-left-right",
      "bank",
      "cash-down",
      "cash-stack",
      "cash-time",
      "cash-scissors",
      "cash-up",
      "five-coins",
      "invoice",
      "chart-up",
      "card-pen",
      "coins",
      "network-money",
      "money-bag",
      "piggy-bank",
      "wallet",
      "cash",
      "percent",
      "hand-coins",
      "safe",
      "bank-home",
      "savings",
      "salary",
      "badge-cent",
      "badge-dollar",
      "badge-euro",
      "badge-pound",
      "badge-ruble",
      "badge-franc",
      "banknote-in",
      "banknote-out",
      "bitcoin",
      "circle-dollar",
      "currency",
      "dollar",
      "euro",
      "pound",
      "ruble",
      "receipt",
      "calculator",
      "chart-pie",
      "trending-up",
      "trending-down",
      "wallet-cards",
      "wallet-minimal",
      "vault",
      "university",
    ],
  },
  {
    title: "People",
    ids: [
      "hands",
      "doctor",
      "id-card",
      "people",
      "person-refresh",
      "person",
      "stroller",
      "user-tie",
      "health",
      "ambulance",
      "briefcase-medical",
      "cross",
      "heart",
      "hospital",
      "pill",
      "pill-bottle",
      "syringe",
      "baby",
      "backpack",
      "footprints",
      "glasses",
      "helping-hand",
      "medal",
      "school",
      "trophy",
      "user-round",
      "users-round",
    ],
  },
  {
    title: "Home",
    ids: [
      "home",
      "building",
      "buildings",
      "sofa",
      "lightning",
      "tv",
      "wifi",
      "air-vent",
      "armchair",
      "bath",
      "blinds",
      "brick-wall",
      "construction",
      "drill",
      "fan",
      "heater",
      "house-plug",
      "house-wifi",
      "lamp",
      "lamp-desk",
      "lightbulb",
      "microwave",
      "paint-bucket",
      "paintbrush",
      "plug-zap",
      "refrigerator",
      "washing-machine",
    ],
  },
] as const;

const groupedIconIds: Set<string> = new Set(categoryIconGroupDefinitions.flatMap((group) => group.ids));
const categoryIconOptionById = new Map(categoryIconOptions.map((icon) => [icon.id, icon]));

export const categoryIconGroups: CategoryIconGroup[] = [
  ...categoryIconGroupDefinitions.map((group) => ({
    title: group.title,
    icons: group.ids.map((id) => categoryIconOptionById.get(id)).filter((icon): icon is CategoryIconOption => Boolean(icon)),
  })),
  {
    title: "Other",
    icons: categoryIconOptions.filter((icon) => !groupedIconIds.has(icon.id)),
  },
];

export const categoryIconAliases: Record<string, string> = {
  "badge-percent": "percent",
  "briefcase-business": "briefcase",
  "heart-pulse": "health",
  "shopping-cart": "grocery",
  plane: "airplane",
  repeat: "arrows-left-right",
  utensils: "food",
};

export const categoryIconById = Object.fromEntries(categoryIconOptions.map((icon) => [icon.id, icon.Icon])) as Record<string, CategoryIconComponent>;

export function normalizeCategoryIconId(icon?: string | null) {
  if (icon && categoryIconById[icon]) return icon;
  return categoryIconAliases[String(icon)] || "tag";
}

export function getCategoryIconComponent(icon?: string | null) {
  return categoryIconById[normalizeCategoryIconId(icon)] || Tag;
}
